import { AnyFieldMeta } from '@tanstack/react-form'
import clsx from 'clsx'
import React from 'react'

import { useTranslation } from '@Globals/Translations'

import styles from './styles.module.scss'

type FieldState = 'default' | 'error' | 'success'

export function metaToFieldState(meta: AnyFieldMeta): FieldState {
  if (meta.isValid && (meta.isTouched || meta.isDefaultValue)) {
    return 'success'
  }

  if (!meta.isValid && meta.isTouched) {
    return 'error'
  }

  return 'default'
}

export const InputGroup = ({
  children,
  className,
  state = 'default',
  ...props
}: React.ComponentProps<'div'> & {
  state?: FieldState
}) => (
  <div
    className={clsx(styles.inputGroup, className)}
    data-state={state}
    {...props}
  >
    {children}
  </div>
)

/**
 * InputGroupBase is needed when using addons together with a label and input to ensure proper alignment and spacing.
 */
export const InputGroupBase = ({
  children,
  className,
  ...props
}: React.ComponentProps<'div'>) => (
  <div className={clsx(styles.inputGroupBase, className)} {...props}>
    {children}
  </div>
)

export const InputGroupInput = ({
  children,
  className,
  placeholder = ' ',
  ...props
}: React.ComponentProps<'input'>) => (
  <input
    className={clsx(styles.inputGroupInput, className)}
    data-slot="input-group-control"
    placeholder={placeholder}
    {...props}
  />
)

export const InputGroupLabel = ({
  children,
  className,
  ...props
}: React.ComponentProps<'label'>) => (
  // eslint-disable-next-line jsx-a11y/label-has-associated-control
  <label
    className={clsx(styles.inputGroupLabel, className)}
    {...props}
  >
    {children}
  </label>
)

export const InputGroupAddon = ({
  align = 'inline-start',
  children,
  className,
  onClick,
  ...props
}: React.ComponentProps<'div'> & {
  align?: 'block-start' | 'block-end' | 'inline-start' | 'inline-end'
}) => (
  <div
    className={clsx(styles.inputGroupAddon, className)}
    data-align={align}
    onClick={(e) => {
      if (onClick) {
        onClick(e)
        return
      }

      if ((e.target as HTMLElement).closest('button')) {
        return
      }

      e.currentTarget.parentElement?.querySelector('input')?.focus()
    }}
    {...props}
  >
    {children}
  </div>
)

export const InputGroupButton = ({
  children,
  className,
  ...props
}: React.ComponentProps<'button'>) => (
  // eslint-disable-next-line react/button-has-type
  <button
    className={clsx(styles.inputGroupButton, className)}
    {...props}
  >
    {children}
  </button>
)

export const InputGroupIcon = ({
  children,
  className,
  ...props
}: React.ComponentProps<'div'>) => (
  <div className={clsx(styles.inputGroupIcon, className)} {...props}>
    {children}
  </div>
)

export const InputErrors = ({
  className,
  errors,
  isTouched,
  isValid,
  ...props
}: React.ComponentProps<'p'> & {
  errors: (string | undefined)[]
  isTouched: boolean
  isValid: boolean
}) => {
  const { t } = useTranslation()

  if (isValid || !isTouched) {
    return null
  }

  return (
    <p className={clsx(styles.inputErrors, className)} {...props}>
      {t('common:ERROR_LABEL')}
      {errors && errors.length !== 0 && (
        <>
          {': '}
          {errors.filter(Boolean).join(', ')}
        </>
      )}
    </p>
  )
}
